import React from 'react';
import { FiUsers, FiPackage, FiDollarSign, FiHeadphones } from 'react-icons/fi';
import IconWrapper from '../IconWrapper';

export const ncdPrinciples = [
  {
    id: 'consumer-understanding',
    name: 'Consumer Understanding',
    description: 'Products and services are designed to meet the needs of consumers and marketed in a way they can understand.',
    icon: FiUsers,
    color: '#00E9D8',
    criteria: [
      'Clear and fair terms',
      'Transparent communication',
      'Comprehensible information',
      'Appropriate product design',
      'Suitable customer onboarding'
    ]
  },
  {
    id: 'products-services',
    name: 'Products and Services',
    description: 'Products and services are suitable for consumers and perform as expected.',
    icon: FiPackage,
    color: '#0088FE',
    criteria: [
      'Product suitability',
      'Quality standards adherence',
      'Operational reliability',
      'Secure systems',
      'Product performance'
    ]
  },
  {
    id: 'price-value',
    name: 'Price and Value',
    description: 'Consumers receive fair value and prices are transparent and reasonable.',
    icon: FiDollarSign,
    color: '#FFBB28',
    criteria: [
      'Price transparency',
      'Value proposition',
      'Fee disclosure',
      'Fair pricing',
      'Cost-benefit ratio'
    ]
  },
  {
    id: 'consumer-support',
    name: 'Consumer Support',
    description: 'Consumers receive appropriate support when they need it.',
    icon: FiHeadphones,
    color: '#FF8042',
    criteria: [
      'Timely responses',
      'Resolution efficiency',
      'Accessibility of support',
      'Staff competence',
      'Customer-centered solutions'
    ]
  }
];

export const crossCuttingRules = [
  {
    id: 'communications',
    name: 'Communications',
    description: 'All communications to consumers are clear, fair, and not misleading.'
  },
  {
    id: 'consumer-service',
    name: 'Consumer Service',
    description: 'Firms provide high-quality customer service that meets consumers\' needs throughout their relationship.'
  },
  {
    id: 'fair-value',
    name: 'Fair Value',
    description: 'Products and services are fit for purpose and represent fair value for consumers.'
  }
];

interface NCDPrincipleCardProps {
  principle: typeof ncdPrinciples[0];
  metric?: {
    value: number;
    total: number;
    trend?: number;
    isPositive?: boolean;
  };
  isActive?: boolean;
  onClick?: () => void;
}

export const NCDPrincipleCard: React.FC<NCDPrincipleCardProps> = ({ 
  principle, 
  metric, 
  isActive = false,
  onClick 
}) => {
  return (
    <div 
      className={`bg-white rounded-xl shadow-sm p-5 border-l-4 cursor-pointer transition-all hover:shadow-md ${isActive ? 'border-primary' : 'border-transparent'}`}
      onClick={onClick}
      style={{ borderLeftColor: isActive ? principle.color : 'transparent' }}
    >
      <div className="flex items-start">
        <div 
          className="p-3 rounded-lg mr-4" 
          style={{ backgroundColor: `${principle.color}20`, color: principle.color }}
        >
          <IconWrapper icon={principle.icon} size={24} />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-secondary mb-1">{principle.name}</h3>
          <p className="text-sm text-secondary/70 line-clamp-2">{principle.description}</p>
          
          {metric && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <div>
                  <span className="text-2xl font-bold">{metric.value}</span>
                  <span className="text-secondary/70 text-sm ml-1">/{metric.total}</span>
                </div>
                
                {metric.trend !== undefined && (
                  <div className={`text-xs font-medium ${metric.isPositive ? 'text-green-500' : 'text-red-500'}`}>
                    {metric.isPositive ? '↑' : '↓'} {metric.trend}%
                  </div>
                )}
              </div>
              
              <div className="mt-2 bg-gray-100 rounded-full h-2 overflow-hidden">
                <div 
                  className="h-full rounded-full" 
                  style={{ 
                    width: `${(metric.value / metric.total) * 100}%`,
                    backgroundColor: principle.color
                  }}
                ></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface NCDPrinciplesGridProps {
  metrics?: Record<string, { value: number; total: number; trend?: number; isPositive?: boolean }>;
  activePrinciple?: string;
  onSelectPrinciple?: (id: string) => void;
}

export const NCDPrinciplesGrid: React.FC<NCDPrinciplesGridProps> = ({ 
  metrics,
  activePrinciple,
  onSelectPrinciple
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {ncdPrinciples.map(principle => (
        <NCDPrincipleCard 
          key={principle.id}
          principle={principle}
          metric={metrics?.[principle.id]}
          isActive={activePrinciple === principle.id}
          onClick={() => onSelectPrinciple?.(principle.id)}
        />
      ))}
    </div>
  );
};

export default NCDPrinciplesGrid;